import { Paperclip } from 'lucide-react';

import { Card } from '@/components/ui/card';

/**
 * Everything the vendor offered, as a planner needs to read it.
 *
 * The headline above this — one number, the cheapest package — is what every
 * list view needs and what a planner glances at. It is not what they *decide*
 * with. A photographer quoting three tiers is asking a question, and answering
 * it means seeing what separates $4,649 from $5,549: which one has the second
 * shooter, which includes the album, what an extra hour costs. Until this
 * existed the tree was extracted, validated, persisted and served, and then
 * rendered as the words "3 packages".
 *
 * Read-only on purpose. Correcting what the model read is the review editor's
 * job (spec phase 2) and it carries `reviewStatus`, an accuracy diff and a
 * write path; a planner who cannot yet see their options is not helped by
 * being told they may edit them.
 */

/** Dollars, as the API serves them — never cents. `quote-resolve.ts` owns the
 *  one conversion boundary and everything client-side is past it. */
const money = (amount: number, currency: string) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    // Whole dollars unless the vendor priced in cents. A rate card of
    // "$475.00 / hr" next to "$5,549.00" is four characters of noise per row
    // in a list whose whole job is comparison.
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(amount);

interface Item {
  id: string;
  name: string;
  description: string | null;
  quantity: number | null;
  unitCost: number;
  section: string | null;
}

/**
 * What a line costs, which is not what one of it costs.
 *
 * `packageTotal` sums `unitCost * (quantity ?? 1)`, so a row printed at its
 * unit price does not add up to the figure above it: an assistant at $200
 * with a quantity of 5 rendered as "Assistant × 5 … $200" beside a $9,755
 * total, and the itemisation was $1,000 short of its own heading. A planner
 * checking the vendor's arithmetic finds ours broken instead.
 */
const lineTotal = (item: Item) => item.unitCost * (item.quantity ?? 1);

/** The unit price, said once, where showing only the extended total would
 *  otherwise hide what the vendor actually quoted per unit. */
function unitNote(item: Item, currency: string): string | null {
  if (item.quantity === null || item.quantity === 1) return null;
  return `${money(item.unitCost, currency)} each`;
}

interface Charge {
  id: string;
  type: string;
  description: string | null;
  amount: number;
}

export interface QuoteBreakdownQuote {
  currency: string;
  confidence: string | null;
  source: 'user' | 'extracted';
  packages: {
    id: string;
    name: string;
    description: string | null;
    statedTotal: number | null;
    total: number | null;
    isSelected: boolean;
    lineItems: Item[];
    additionalCharges: Charge[];
  }[];
  addOns: Item[];
  additionalCharges: Charge[];
}

export default function QuoteBreakdown({
  quote,
  attachmentName,
}: {
  /** Tolerant of a quote with no tree arrays at all, which the served type
   *  says cannot happen and an older payload does anyway. */
  quote: QuoteBreakdownQuote;
  /** The document this was read out of, when it was read out of one. Named
   *  rather than linked: the bytes were never stored, so there is nothing here
   *  to open — the file is in the conversation, where the vendor sent it. */
  attachmentName?: string | null;
}) {
  const packages = quote.packages ?? [];
  /** One offer is a price; several are a choice, and they are not the same
   *  thing to look at. */
  const single = packages.length === 1;
  const addOns = quote.addOns ?? [];
  const charges = quote.additionalCharges ?? [];

  if (packages.length === 0 && addOns.length === 0 && charges.length === 0) {
    return (
      <Card className="rounded-3xl bg-card p-5 shadow-ambient">
        <p className="text-sm text-muted-foreground">
          {quote.source === 'user'
            ? 'You set this figure by hand, so there is no breakdown behind it.'
            : 'One figure, with no packages broken out behind it.'}
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {packages.length > 0 && (
        <section className="space-y-2">
          {/* A card per package is the shape of a *choice* — several objects,
              side by side, one of which gets picked. A vendor who quoted one
              price is not offering a choice, and putting their single offer in
              the same bubble says they are: the planner starts looking for the
              other cards. So one offer is laid out as the quote itself, and
              only two or more take the grid.

              A table was considered for the multiple case and rejected: it
              makes the options read as entries in a ledger, and every package
              carries prose — inclusions, a description — that a cell cannot
              hold. */}
          <div className={single ? undefined : 'grid gap-3 lg:grid-cols-2'}>
            {packages.map((pkg) => {
              /**
               * A plain `div` for the lone offer rather than a `Card` told to
               * undo itself. `Card`'s base carries `shadow-ambient`, which
               * tailwind-merge does not recognise as a shadow utility and so
               * cannot collapse against `shadow-none` — the class survived,
               * and the result was a soft rectangle floating behind content
               * with no card under it. Unstyling a styled component is a bet
               * on a merge that nobody can see in the JSX; this is not.
               */
              const Frame = single ? 'div' : Card;
              return (
              <Frame
                key={pkg.id}
                className={
                  single
                    ? 'flex flex-col gap-3'
                    : 'flex flex-col gap-3 rounded-3xl bg-card p-5 shadow-ambient'
                }
              >
                <div className="flex items-baseline justify-between gap-3">
                  {/* The one offer carries the weight the grid would otherwise
                      give it: nothing beside it is competing for the eye, so
                      the figure can be the largest thing on the panel. */}
                  <h4
                    className={
                      single
                        ? 'display-tight text-lg font-semibold'
                        : 'display-tight text-base font-semibold'
                    }
                  >
                    {pkg.name}
                  </h4>
                  <p
                    className={
                      single
                        ? 'display-tight shrink-0 text-2xl font-semibold tabular-nums'
                        : 'display-tight shrink-0 text-lg font-semibold tabular-nums'
                    }
                  >
                    {pkg.total === null ? '—' : money(pkg.total, quote.currency)}
                  </p>
                </div>

                {pkg.description && (
                  <p className="text-sm leading-6 text-muted-foreground">{pkg.description}</p>
                )}

                {/* A single line item that costs exactly what the package
                    costs is the package, written twice. The extractor emits
                    one routinely — a tier whose price is printed once gets a
                    synthetic "<name> Package" item to carry it — and showing
                    both makes a planner read the same figure twice and look
                    for the difference. A real breakdown has more than one
                    line, or a line that is part of the whole. */}
                {pkg.lineItems.length > 0 &&
                  !(
                    pkg.lineItems.length === 1 &&
                    pkg.lineItems[0] &&
                    lineTotal(pkg.lineItems[0]) === pkg.total
                  ) && (
                  <ul className="space-y-1 border-t border-border pt-3">
                    {pkg.lineItems.map((item) => (
                      <li key={item.id} className="flex items-baseline justify-between gap-3 text-sm">
                        <span className="min-w-0">
                          {item.name}
                          {/* A quantity only when the vendor gave one. "1 ×"
                              in front of every line is noise that reads as
                              precision. */}
                          {item.quantity !== null && item.quantity !== 1 && (
                            <span className="text-muted-foreground"> × {item.quantity}</span>
                          )}
                          {unitNote(item, quote.currency) && (
                            <span className="block text-xs text-muted-foreground">
                              {unitNote(item, quote.currency)}
                            </span>
                          )}
                        </span>
                        <span className="shrink-0 tabular-nums text-muted-foreground">
                          {money(lineTotal(item), quote.currency)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}

                {pkg.additionalCharges.length > 0 && (
                  <ul className="space-y-1 text-sm">
                    {pkg.additionalCharges.map((charge) => (
                      <li key={charge.id} className="flex items-baseline justify-between gap-3">
                        <span className="text-muted-foreground">
                          {charge.description ?? charge.type}
                        </span>
                        <span className="shrink-0 tabular-nums text-muted-foreground">
                          {money(charge.amount, quote.currency)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </Frame>
              );
            })}
          </div>
        </section>
      )}

      {addOns.length > 0 && (
        <section className="space-y-2">
          {/* Not packages and not part of any total: these are priced
              separately and chosen separately, which is why they sit outside
              the cards above rather than repeated inside each one. */}
          <h3 className="label-caps text-xs text-muted-foreground">Priced separately</h3>
          <Card className="rounded-3xl bg-card p-5 shadow-ambient">
            <ul className="space-y-2">
              {addOns.map((item) => (
                <li key={item.id} className="flex items-baseline justify-between gap-3 text-sm">
                  <span className="min-w-0">
                    {item.name}
                    {item.description && (
                      <span className="block text-xs text-muted-foreground">
                        {item.description}
                      </span>
                    )}
                  </span>
                  <span className="shrink-0 tabular-nums">
                    {money(lineTotal(item), quote.currency)}
                  </span>
                </li>
              ))}
            </ul>
          </Card>
        </section>
      )}

      {charges.length > 0 && (
        <section className="space-y-2">
          {/* Quote-scoped, so deliberately not folded into any package total —
              a delivery fee billed once whichever tier is chosen would
              otherwise appear to be part of all three. */}
          <h3 className="label-caps text-xs text-muted-foreground">Whichever option you take</h3>
          <Card className="rounded-3xl bg-card p-5 shadow-ambient">
            <ul className="space-y-2">
              {charges.map((charge) => (
                <li key={charge.id} className="flex items-baseline justify-between gap-3 text-sm">
                  <span>{charge.description ?? charge.type}</span>
                  <span className="shrink-0 tabular-nums">
                    {money(charge.amount, quote.currency)}
                  </span>
                </li>
              ))}
            </ul>
          </Card>
        </section>
      )}

      {attachmentName && (
        <p className="flex items-center gap-2 text-xs text-muted-foreground">
          <Paperclip className="size-3 shrink-0" />
          Read from {attachmentName}
        </p>
      )}
    </div>
  );
}
